<template>
  <div class="section container">
    <div class="section-header">
      <div class="section-title">
        <div class="section-icon section-icon-localization" :class="brandClass()"></div>
        <h3 class="section-title-label">
          {{$t("profile_account_data_localization_summary_title")}}
        </h3>
      </div>
      <div @keydown="ariaClick" role="button" tabindex="0" @click="handleEditMode()" class="section-edit pointer" :class="{'hidden': editDisabled}" v-if="!editMode">
        <div class="section-edit-icon"></div>
        <div class="section-edit-label">
          {{$t("profile_edit_button")}}
        </div>
      </div>
    </div>
    <div class="section-sub-header">
      {{$t("profile_account_data_localization_summary_subtitle")}}
    </div>
    <form v-if="editMode" novalidate class="form" @submit.prevent="validateLocalization">
      <div class="custom-field">
        <OneSelect
          :brandClass="brandClass()"
          :t="$t"
          :profileStyle="true"
          v-model="form.localization"
          :form="v$.form"  
          :paramName="'localization'"
          :disabled="isDropdownDisabled"
          :options="getSelectOptions()"
          @blur="touchForm('localization')"
          :isSearchEnabled="true"
          :label="$t('profile_account_data_localization')+'*'"
          :errors="[
            {message:$t('profile_account_data_edit_localization_error_required'), thereIsTheError:v$.form.localization.required.$invalid}
          ]"
        ></OneSelect>
      </div>
      <div class="mandatory" style="order:100">*{{$t("generic_mandatory_fields")}}</div>
      <div 
        v-if="showNotificationBox" 
        style="margin-top: 1rem">
        <NotificationBox :messageTextKey="
          showOldMessage ? 
            'profile_account_data_localization_cannot_edit' : 
            'profile_account_data_localization_cannot_edit_country'"
        />
      </div>
      <div class="buttonsContainer flex_button_container">
        <OneButton
              :brandClass="brandClass()"
          :profileStyle="true"
          :type="'primary'"
          :label="$t('profile_save_button')"
          @click="validateLocalization()"
          :disabled="isButtonDisabled"
        />
        <OneButton
              :brandClass="brandClass()"
          :profileStyle="true"
          :type="'secondary'"
          :label="$t('profile_cancel_button')"
          @click="handleEditMode(-1)"
        />
        
      </div>
    </form>
    <div v-if="!editMode">
      <div class="infos localization-information">
        <div class="info">
          <div class="label localization-label">{{$t("profile_account_data_localization")}}</div>
          <div class="value localization-value">{{getLocalizationInfo()}}</div>
        </div>
      </div>
    </div>
    <hr alt="" role="presentation">
  </div>
</template>
<style lang="scss" scoped>

.section-icon-localization {
  background-image: url('~@/assets/default/light/localization-default-light.svg');
  &.__d{
    background-image: url('~@/assets/default/dark/localization-default-dark.svg');
  }
  &.__m{
    background-image: url('~@/assets/mini/light/localization-mini-light.svg');  
    &.__d{
      background-image: url('~@/assets/mini/dark/localization-mini-dark.svg');
    }                                
  }

  &.__rr {
    background-image: url('~@/assets/default/dark/localization-default-dark.svg');
  }

  &.__alphabet {
    background-image: url('~@/assets/alphabet/light/localization-alphabet-light.svg');
  }
}
</style>
<script>
import useVuelidate from '@vuelidate/core'
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";

import NotificationBox from "@/components/ProfileForm/components/NotificationBox";

import {
  required,
} from "@vuelidate/validators";

export default {
  inject:['$c', '$t', 'brandClass'],
  setup() {
    return { v$: useVuelidate() }
  },
  name: "ProfileLocalization",
  components: {
    NotificationBox
  },
  //validations from vuelidate
  mixins: [oneUtils.customValidationMixin, oneUtils.accessibilityMixin],
  props: {
    user: {},
    editDisabled: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    form: {
      localization: null,
    },
    editMode: false,
  }),
  //vuelidate custom validations
  validations: {
    form: {
      localization: {
        required,
      },
    },
  },
  computed: {
    isButtonDisabled: function () {
      return this.v$.$invalid;
    },
    linkedAccounts: function () {
      return this.user.linkedAccounts;
    },
    isDropdownDisabled: function() {
      return this.getSelectOptions().length < 2;
    },
    showNotificationBox: function () {
      return this.user?.linkedAccounts?.find(linked => linked.type === 'CONNECTED_MAPPING' || linked.type === 'CONNECTED_SUBSCRIBED') ? true : false;
    },
    showOldMessage() {
      return !this.$c("list_of_localization").find(localization => localization.countryKey === this.user.homeMarket) ? true : false;
    }
  },
  mounted() {
    this.prefillForm();
  },
  methods: {
    /**
     * Return country localization
     */
    getSelectOptions() {
      const localization = this.getLocalizationList();
      const options = [];
      for (const loc of localization) {
        const option = {
          label: `${loc.countryKey} - ${this.$t(loc.translationKey)}`,
          value: `${loc.countryKey}-${loc.languageKey}`,
        };
        options.push(option);
      }
      return options;
    },
    /**
     * [GCDM-271592] Implementation
     */
    getLocalizationList() {
      if(
        this.user.linkedAccounts && 
        this.user.linkedAccounts.length > 0 &&
        this.user.linkedAccounts.find(linked => linked.type === 'CONNECTED_MAPPING' || linked.type === 'CONNECTED_SUBSCRIBED')
      ){
        return this.$c("list_of_localization").filter(localization => localization.countryKey === this.user.homeMarket);
      }else
        return this.$c("list_of_localization");
    },
    /**
     * Return user country localization
     */
    getLocalizationInfo(){
      const language = this.user.correspondLanguageISO?.toLowerCase();
      const country = this.user.homeMarket;
      if (language && country) {
        const options = this.getSelectOptions();
        const localization = country+"-"+language;
        if (this.user && language && country && options.some(o => o.value === localization)) {
          return options.find(o => o.value === localization).label;
        }
      }
      return this.$c('show_homemarket_selection_button') ? this.$t('profile_account_data_localization_summary_please_select_localization') : '-';
    },
    /**
     * Prefill form with user value
     */
    prefillForm: function () {
      const language = this.user.correspondLanguageISO?.toLowerCase();
      const country = this.user.homeMarket;
      const options = this.getSelectOptions();
      const localization = country + "-" + language;
      if (this.user && language && country && options.some(o => o.value === localization)) {
        this.form.localization = options.find(o => o.value === localization).value;
      }
      this.touchForm("localization");
    },
    /**
     * call update user value function
     * Edit managment
     */
    validateLocalization() {
      this.submitUpdate();
      this.handleEditMode();
    },
    /**
     * update user API
     * error management
     */
    submitUpdate() {
      oneUtils.utils.showGlobalLoader();
      this.isLoading = true;
      this.user.homeMarket = this.form.localization.split('-')[0];
      this.user.correspondLanguageISO = this.form.localization.split('-')[1].toUpperCase();
      oneUtils.httpClient
        .updateUser(window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token)
        .then(() => {
          oneUtils.utils.showMessage("SUCCESS", this.$t("profile_user_success"));
        })
        .catch((err) => {
          oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
        })
        .finally(() => {
          oneUtils.utils.hideGlobalLoader();
        });
    },
    /**
     * Edit mode valorization
     * prefill form if editMode = false
     */
    handleEditMode() {
      this.editMode = !this.editMode;
      if (!this.editMode) {
        this.prefillForm();
      }
    },
  },
};
</script>
